import React, { useRef, useState } from 'react';
import { getPartLabel } from '../../buttons/ColoredPartSelecter';
import { logEvent, logOptionChange } from '../../../utils/experimentLog';
import SelfExpTemplate from './SelfExpTemplate';
import SelfExpSelect from './SelfExpSelect';
import { getSelfExpOptions } from './selfExpOptions';
import { useSelfExpSubmit } from './useSelfExpSubmit';
import ExerciseAdvanceDialog from './ExerciseAdvanceDialog';

// 自己説明の「説明シート」（インライン版）。ShapeChange / GazeControl の右カラムに
// 常時表示し、シミュレーションを見ながら説明を組み立てられるようにする（旧：完了ボタン→モーダル）。
// 「決定」で回答を送信し、ExerciseAdvanceDialog を挟んでから onAdvance で次の問へ進む。
// 送信した時点で onSubmitted(detail) を呼ぶ（親が回答済みとして保存する。ダイアログの間にリロードしても
// 答え直しにならないように。2026-10-12）。
// demo: 研究発表用デモ（/suggest2・/demo3）で見た目だけ再現するモード。
//   プルダウンは操作できるが【決定】を押しても何も起こらず、実験ログも送らない
//   （デモ操作が Supabase・Flask の測定データに混ざらないようにする）。
// initialAnswer / locked: 回答済みの問へタブで戻ってきたとき。当時の回答
//   （プルダウンの選択・記述）を復元して見せ、【決定】だけ押せなくする。
function SelfExpPanel({
  number,
  phase,
  errorGlbPath,
  selectedPart,
  isLastQuestion = false,
  demo = false,
  initialAnswer = null,
  locked = false,
  onAdvance,
  onSubmitted, // 【決定】で送信した直後に呼ぶ（X-2。親が回答済みとして保存する）
  onSelectionChange, // 空欄の選択が変わるたびに呼ぶ（X-2 の手順の進み具合に使う。{ complete, filled, total, ... }）
}) {
  const [text, setText] = useState(() => (initialAnswer && initialAnswer.text) || '');
  // 「説明例」を1回でも開いたか。自力回答とテンプレ参照後の回答を分析で切り分ける
  const [templateUsed, setTemplateUsed] = useState(false);
  // 選択式回答（SelfExpSelect）の状態。候補データがあるモデルのみ使う
  const [selectResult, setSelectResult] = useState({ text: '', complete: false, filled: 0, total: 0 });
  // 直前の空欄の選択。選び直しを option_changes に記録するために比べる
  const prevSelectionsRef = useRef((initialAnswer && initialAnswer.selections) || {});
  // 送信済み＝確認ダイアログ表示中。回答内容は onAdvance までここで保持する
  const [submittedAnswer, setSubmittedAnswer] = useState(null);

  const { prompt, submitting, submit } = useSelfExpSubmit({ number, phase, errorGlbPath, selectedPart });

  const partLabel = getPartLabel(selectedPart) || '（未選択）';
  // 候補語彙が用意されているモデルは選択式（最も制約された版）、無いモデルは従来の自由記述
  const options = getSelfExpOptions(phase, errorGlbPath);
  const isSelectMode = Boolean(options);
  const canSubmit = !locked && (isSelectMode ? selectResult.complete : Boolean(text.trim()));
  // 空欄数＝スロット数。1つも選ぶ前（selectResult.total が 0）でも進捗を出せるようにする
  const totalBlanks = selectResult.total || (options ? Object.keys(options).length : 0);

  const handleSelectChange = (result) => {
    setSelectResult(result);
    if (typeof onSelectionChange === 'function') onSelectionChange(result);
    const prev = prevSelectionsRef.current;
    const next = result.selections || {};
    if (!demo && !locked) {
      Object.keys(next).forEach((slot) => {
        if (next[slot] && next[slot] !== prev[slot]) logOptionChange(slot, next[slot], prev[slot]);
      });
    }
    prevSelectionsRef.current = next;
  };

  const handleTemplateOpen = () => {
    setTemplateUsed(true);
    if (demo) return;
    logEvent('selfexp_template_open', {
      questionNumber: number ?? null,
      glbPath: errorGlbPath,
      selectedPart,
      phase,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // デモでは押しても何も起こらない（送信・ログ・画面遷移のいずれも行わない）
    if (demo) return;
    if (!canSubmit || submitting) return;
    const answer = await submit({
      answerText: isSelectMode ? selectResult.text : text,
      answerMode: isSelectMode ? 'select' : 'free',
      selectedOptions: isSelectMode ? selectResult.selections : null,
      templateUsed,
    });
    // 回答内容は親（演習ルート）が保存し、戻ってきたときの再表示に使う
    const detail = { text: answer, selections: isSelectMode ? selectResult.selections : null };
    if (typeof onSubmitted === 'function') onSubmitted(detail);
    setSubmittedAnswer(detail);
  };

  return (
    <>
      <form className="selfexp-panel" onSubmit={handleSubmit}>
        <header className="selfexp-panel__head">
          <p className="selfexp-panel__eyebrow">説明シート</p>
          <h2 className="selfexp-panel__title">この誤りの原因と直し方を説明しよう</h2>
        </header>

        <div className="selfexp-panel__body">
          <div className="selfexp-panel__part">
            <span className="selfexp-panel__part-label">選んだ部品</span>
            <span className="selfexp-part-chip">{partLabel}</span>
          </div>
          <p className="selfexp-panel__prompt">{prompt}</p>

          {isSelectMode ? (
            <SelfExpSelect
              phase={phase}
              errorGlbPath={errorGlbPath}
              onChange={handleSelectChange}
              initialSelections={initialAnswer && initialAnswer.selections}
              disabled={locked}
            />
          ) : (
            <>
              <SelfExpTemplate phase={phase} onOpen={handleTemplateOpen} />
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="どう直せばモチーフに近づくか、あなたの言葉で説明してください..."
                rows={8}
                className="selfexp-panel__textarea"
                readOnly={locked}
              />
            </>
          )}
        </div>

        <footer className="selfexp-panel__foot">
          {isSelectMode && totalBlanks > 0 && (
            <div className="selfexp-progress">
              <span className="selfexp-progress__track">
                <span
                  className="selfexp-progress__bar"
                  style={{ width: `${(selectResult.filled / totalBlanks) * 100}%` }}
                />
              </span>
              <span className="selfexp-progress__label">
                {selectResult.filled} / {totalBlanks}
              </span>
            </div>
          )}
          <button
            type="submit"
            className={`btn-sheet btn-sheet--block${canSubmit && !submitting ? ' btn-sheet--attention' : ''}`}
            disabled={!canSubmit || submitting}
          >
            {locked ? '回答済み' : submitting ? '保存中...' : '決定'}
          </button>
          {!canSubmit && (
            <p className="selfexp-panel__hint">
              {locked
                ? 'この問題は回答済みです。内容の確認だけできます'
                : isSelectMode
                  ? '空欄（？？？）をすべて選ぶと【決定】が押せます'
                  : '説明を書くと【決定】が押せます'}
            </p>
          )}
        </footer>
      </form>

      {submittedAnswer !== null && (
        <ExerciseAdvanceDialog
          isLast={isLastQuestion}
          onAdvance={() => {
            if (typeof onAdvance === 'function') onAdvance(submittedAnswer.text, submittedAnswer);
          }}
        />
      )}
    </>
  );
}

export default SelfExpPanel;
